import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { DraftPair } from "../types/challengeDraft";
import { shuffleList } from "./challengePairs";

export const POCO_COURTS = 3;
export const POCO_ROUND_DURATION_MINUTES = 20;
export const POCO_MAX_DURATION_MINUTES = 120;
export const POCO_ROUNDS = POCO_MAX_DURATION_MINUTES / POCO_ROUND_DURATION_MINUTES;
export const POCO_MAX_PAIRS = 6;

export function normalizePocoCourtsCount(value: unknown) {
  const parsed = Math.floor(Number(value));
  return parsed === 4 ? 4 : POCO_COURTS;
}

export function getPocoPairsCount(courtsCount: unknown) {
  return normalizePocoCourtsCount(courtsCount) * 2;
}

// Court count changes the number of simultaneous matches, never the playing time.
export function getPocoRoundsCount(_courtsCount?: unknown) {
  return POCO_ROUNDS;
}

export function getPocoTotalPlayersCapacity({
  courtsCount,
  playersCount,
  registrationCategories,
}: {
  courtsCount: unknown;
  playersCount?: unknown;
  registrationCategories?: Array<{ slotLimit?: unknown }> | null;
}) {
  const categoryPairSlots = (registrationCategories || []).reduce(
    (total, category) => {
      const slotLimit = Math.floor(Number(category?.slotLimit));
      return Number.isFinite(slotLimit) && slotLimit > 0
        ? total + slotLimit
        : total;
    },
    0,
  );
  if (categoryPairSlots > 0) return categoryPairSlots * 2;

  const configuredPlayers = Math.floor(Number(playersCount));
  if (Number.isFinite(configuredPlayers) && configuredPlayers >= 12) {
    return configuredPlayers;
  }
  return normalizePocoCourtsCount(courtsCount) * 4;
}

export function getPocoTotalRemainingPlayers(
  registrationCategories: Array<{
    remainingSlots?: unknown;
    slotLimit?: unknown;
  }> | null | undefined,
) {
  return (registrationCategories || []).reduce((total, category) => {
    const remainingPairs = Math.max(
      0,
      Math.floor(Number(category?.remainingSlots ?? category?.slotLimit ?? 0)),
    );
    return total + remainingPairs * 2;
  }, 0);
}

export type AdminRoundMatch = {
  dupla1Id: string;
  dupla2Id: string;
  score1: number | "";
  score2: number | "";
  categoryKey?: string;
};

export type PocoStandingRow = {
  id: string;
  points: number;
  roundPoints: number[];
  gamesWon: number;
  gamesLost: number;
  gamesBalance: number;
  wins: number;
  losses: number;
};

export function buildEmptyPocoMatch(
  dupla1Id = "",
  dupla2Id = "",
): AdminRoundMatch {
  return {
    dupla1Id,
    dupla2Id,
    score1: "",
    score2: "",
  };
}

export function toNumericScore(value: number | "" | undefined) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function resolvedWinnerLoser(match?: AdminRoundMatch) {
  if (!match?.dupla1Id || !match?.dupla2Id) return null;
  const score1 = toNumericScore(match.score1);
  const score2 = toNumericScore(match.score2);
  if (score1 === null || score2 === null || score1 === score2) return null;
  return score1 > score2
    ? { winner: match.dupla1Id, loser: match.dupla2Id }
    : { winner: match.dupla2Id, loser: match.dupla1Id };
}

export function resolvePocoPoints(
  courtIndex: number,
  isWinner: boolean,
  courtsCount: unknown = POCO_COURTS,
) {
  if (!isWinner) return 0;
  const normalizedCourtsCount = normalizePocoCourtsCount(courtsCount);
  if (courtIndex < 0 || courtIndex >= normalizedCourtsCount) return 0;
  return (normalizedCourtsCount - courtIndex) * 2 - 1;
}

export function calculatePocoStandings(
  rounds: AdminRoundMatch[][],
  options: { includeRoundIndexes?: number[] } = {},
) {
  const includeRoundSet = Array.isArray(options.includeRoundIndexes)
    ? new Set(options.includeRoundIndexes)
    : null;
  const rowsById = new Map<string, PocoStandingRow>();
  const matches: Array<{
    roundIndex: number;
    courtIndex: number;
    courtsCount: number;
    match: AdminRoundMatch;
  }> = [];

  (rounds || []).forEach((round, roundIndex) => {
    (round || []).forEach((match, courtIndex) => {
      const dupla1Id = String(match?.dupla1Id || "").trim();
      const dupla2Id = String(match?.dupla2Id || "").trim();
      if (!dupla1Id || !dupla2Id) return;
      if (!rowsById.has(dupla1Id)) {
        rowsById.set(dupla1Id, {
          id: dupla1Id,
          points: 0,
          roundPoints: [],
          gamesWon: 0,
          gamesLost: 0,
          gamesBalance: 0,
          wins: 0,
          losses: 0,
        });
      }
      if (!rowsById.has(dupla2Id)) {
        rowsById.set(dupla2Id, {
          id: dupla2Id,
          points: 0,
          roundPoints: [],
          gamesWon: 0,
          gamesLost: 0,
          gamesBalance: 0,
          wins: 0,
          losses: 0,
        });
      }
      const score1 = toNumericScore(match.score1);
      const score2 = toNumericScore(match.score2);
      if (score1 === null || score2 === null || score1 === score2) return;
      matches.push({
        roundIndex,
        courtIndex,
        courtsCount: round.length,
        match,
      });
    });
  });

  matches.forEach((item) => {
    if (includeRoundSet && !includeRoundSet.has(item.roundIndex)) return;
    const match = item.match;
    const score1 = toNumericScore(match.score1);
    const score2 = toNumericScore(match.score2);
    if (score1 === null || score2 === null || score1 === score2) return;

    const row1 = rowsById.get(match.dupla1Id);
    const row2 = rowsById.get(match.dupla2Id);
    if (!row1 || !row2) return;

    row1.gamesWon += score1;
    row1.gamesLost += score2;
    row2.gamesWon += score2;
    row2.gamesLost += score1;

    const pair1Won = score1 > score2;
    if (pair1Won) {
      row1.wins += 1;
      row2.losses += 1;
    } else {
      row2.wins += 1;
      row1.losses += 1;
    }

    if (item.roundIndex > 0) {
      const pair1RoundPoints = resolvePocoPoints(
        item.courtIndex,
        pair1Won,
        item.courtsCount,
      );
      const pair2RoundPoints = resolvePocoPoints(
        item.courtIndex,
        !pair1Won,
        item.courtsCount,
      );
      row1.points += pair1RoundPoints;
      row2.points += pair2RoundPoints;
      row1.roundPoints[item.roundIndex] =
        Number(row1.roundPoints[item.roundIndex] || 0) + pair1RoundPoints;
      row2.roundPoints[item.roundIndex] =
        Number(row2.roundPoints[item.roundIndex] || 0) + pair2RoundPoints;
    }
  });

  return Array.from(rowsById.values())
    .map((row) => ({
      ...row,
      gamesBalance: row.gamesWon - row.gamesLost,
    }))
    .sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      const maxRoundIndex = Math.max(
        a.roundPoints.length - 1,
        b.roundPoints.length - 1,
      );
      for (let roundIndex = maxRoundIndex; roundIndex >= 1; roundIndex -= 1) {
        const diff =
          Number(b.roundPoints[roundIndex] || 0) -
          Number(a.roundPoints[roundIndex] || 0);
        if (diff !== 0) return diff;
      }
      return a.id.localeCompare(b.id);
    });
}

export function isPocoRoundResolved(round: AdminRoundMatch[]) {
  return (round || []).every((match) => {
    const dupla1Id = String(match?.dupla1Id || "").trim();
    const dupla2Id = String(match?.dupla2Id || "").trim();
    if (!dupla1Id || !dupla2Id) return false;
    return Boolean(resolvedWinnerLoser(match));
  });
}

export function buildPocoFinalRound(
  roundsBeforeFinal: AdminRoundMatch[][],
  existingFinalRound: AdminRoundMatch[] = [],
) {
  const courtsCount = normalizePocoCourtsCount(roundsBeforeFinal?.[0]?.length);
  const roundsBeforeFinalCount = getPocoRoundsCount(courtsCount) - 1;
  if ((roundsBeforeFinal || []).length < roundsBeforeFinalCount) {
    return [buildEmptyPocoMatch()];
  }
  const roundsForStanding = roundsBeforeFinal.slice(0, roundsBeforeFinalCount);
  const allResolved = roundsForStanding.every((round) =>
    isPocoRoundResolved(round),
  );
  if (!allResolved) {
    return [buildEmptyPocoMatch()];
  }

  const standing = calculatePocoStandings(roundsForStanding, {
    includeRoundIndexes: Array.from(
      { length: Math.max(0, roundsBeforeFinalCount - 1) },
      (_item, index) => index + 1,
    ),
  });
  if (standing.length < 2) {
    return [buildEmptyPocoMatch()];
  }

  const finalistA = standing[0].id;
  const finalistB = standing[1].id;
  const current = existingFinalRound[0];
  const keepScores =
    current && current.dupla1Id === finalistA && current.dupla2Id === finalistB;

  return [
    {
      dupla1Id: finalistA,
      dupla2Id: finalistB,
      score1: keepScores ? current.score1 : "",
      score2: keepScores ? current.score2 : "",
    },
  ];
}

export function isPendingPocoMatch(
  match?: AdminRoundMatch,
  pairLabelById?: Record<string, string>,
) {
  const dupla1Id = String(match?.dupla1Id || "").trim();
  const dupla2Id = String(match?.dupla2Id || "").trim();
  if (!dupla1Id || !dupla2Id) return true;
  if (!pairLabelById) return false;
  const pendingLabel = String(
    siteTexts.challengeEngine.labels.removedPair || "",
  ).trim();
  const label1 = String(pairLabelById[dupla1Id] || pendingLabel).trim();
  const label2 = String(pairLabelById[dupla2Id] || pendingLabel).trim();
  return label1 === pendingLabel && label2 === pendingLabel;
}

export function buildPocoNextRound(
  previousRound: AdminRoundMatch[],
  existingRound: AdminRoundMatch[] = [],
) {
  const courtsCount = normalizePocoCourtsCount(previousRound.length);
  const courts = previousRound.slice(0, courtsCount);
  if (courts.length < courtsCount) {
    return Array.from({ length: courtsCount }, (_item, index) =>
      buildEmptyPocoMatch(
        existingRound[index]?.dupla1Id || "",
        existingRound[index]?.dupla2Id || "",
      ),
    );
  }

  const results = courts.map((court) => resolvedWinnerLoser(court));

  if (results.some((result) => !result)) {
    return Array.from({ length: courtsCount }, () => buildEmptyPocoMatch());
  }

  const resolved = results as Array<{ winner: string; loser: string }>;
  const nextMatches: Array<[string, string]> = [
    [resolved[0].winner, resolved[1].winner],
  ];
  for (let courtIndex = 1; courtIndex < courtsCount - 1; courtIndex += 1) {
    nextMatches.push([
      resolved[courtIndex - 1].loser,
      resolved[courtIndex + 1].winner,
    ]);
  }
  nextMatches.push([
    resolved[courtsCount - 2].loser,
    resolved[courtsCount - 1].loser,
  ]);

  return nextMatches.map(([dupla1Id, dupla2Id], index) => {
    const current = existingRound[index];
    const keepScores =
      current && current.dupla1Id === dupla1Id && current.dupla2Id === dupla2Id;
    return {
      dupla1Id,
      dupla2Id,
      score1: keepScores ? current.score1 : "",
      score2: keepScores ? current.score2 : "",
    };
  });
}

export function rebuildPocoRounds(
  rounds: AdminRoundMatch[][],
) {
  const courtsCount = normalizePocoCourtsCount(rounds?.[0]?.length);
  const targetRounds = POCO_ROUNDS;
  // Do not discard played matches when loading a legacy eight-round schedule.
  if (rounds.length > targetRounds && rounds.slice(targetRounds - 1).some(round =>
    round.some(match =>
      (match.score1 !== "" && match.score1 !== undefined && toNumericScore(match.score1) !== null) ||
      (match.score2 !== "" && match.score2 !== undefined && toNumericScore(match.score2) !== null),
    ),
  )) return rounds;
  const firstRound = Array.isArray(rounds[0])
    ? rounds[0].slice(0, courtsCount)
    : [];
  if (firstRound.length < courtsCount) return rounds;
  const nextRounds: AdminRoundMatch[][] = [firstRound];
  for (let roundIndex = 1; roundIndex < targetRounds - 1; roundIndex += 1) {
    const generated = buildPocoNextRound(
      nextRounds[roundIndex - 1],
      Array.isArray(rounds[roundIndex])
        ? rounds[roundIndex].slice(0, courtsCount)
        : [],
    );
    nextRounds.push(generated);
  }
  const existingFinalRound =
    targetRounds > 0 && Array.isArray(rounds[targetRounds - 1])
      ? rounds[targetRounds - 1].slice(0, 1)
      : [];
  const finalRound = buildPocoFinalRound(nextRounds, existingFinalRound);
  nextRounds.push(finalRound);
  return nextRounds;
}

export function getPocoRoundCategoryKey(round?: AdminRoundMatch[]) {
  const match = (round || []).find((item) =>
    Boolean(String(item?.categoryKey || "").trim()),
  );
  return String(match?.categoryKey || "").trim();
}

export function getPocoCategoryRoundIndex(
  rounds: AdminRoundMatch[][],
  roundIndex: number,
) {
  const targetCategoryKey = getPocoRoundCategoryKey(rounds[roundIndex]);
  let categoryRoundIndex = -1;
  for (let index = 0; index <= roundIndex; index += 1) {
    if (getPocoRoundCategoryKey(rounds[index]) === targetCategoryKey) {
      categoryRoundIndex += 1;
    }
  }
  return categoryRoundIndex;
}

export function rebuildPocoRoundsForCategory(
  rounds: AdminRoundMatch[][],
  targetRoundIndex: number,
) {
  const targetCategoryKey = getPocoRoundCategoryKey(rounds[targetRoundIndex]);
  if (!targetCategoryKey) {
    return rebuildPocoRounds(rounds);
  }

  const categoryRoundIndexes = rounds.reduce<number[]>((indexes, round, index) => {
    if (getPocoRoundCategoryKey(round) === targetCategoryKey) indexes.push(index);
    return indexes;
  }, []);
  const categoryRounds = categoryRoundIndexes.map((index) => rounds[index]);
  const rebuiltCategoryRounds = rebuildPocoRounds(
    categoryRounds,
  ).map((round) =>
    round.map((match) => ({ ...match, categoryKey: targetCategoryKey })),
  );

  const categoryRoundIndexSet = new Set(categoryRoundIndexes);
  const firstCategoryRoundIndex = categoryRoundIndexes[0];
  const rebuilt: AdminRoundMatch[][] = [];
  rounds.forEach((round, index) => {
    if (index === firstCategoryRoundIndex) rebuilt.push(...rebuiltCategoryRounds);
    if (!categoryRoundIndexSet.has(index)) rebuilt.push(round);
  });
  return rebuilt;
}

export function rebuildPocoRoundsByCategory(rounds: AdminRoundMatch[][]) {
  const categoryKeys = Array.from(
    new Set(
      rounds
        .map((round) => getPocoRoundCategoryKey(round))
        .filter(Boolean),
    ),
  );
  if (categoryKeys.length === 0) return rebuildPocoRounds(rounds);

  return categoryKeys.reduce((currentRounds, categoryKey) => {
    const categoryRoundIndex = currentRounds.findIndex(
      (round) => getPocoRoundCategoryKey(round) === categoryKey,
    );
    return categoryRoundIndex >= 0
      ? rebuildPocoRoundsForCategory(currentRounds, categoryRoundIndex)
      : currentRounds;
  }, rounds);
}

export function buildPocoRoundsFromPairs(
  pairs: DraftPair[],
  courtsCount: unknown = POCO_COURTS,
  shufflePairs = true,
) {
  const normalizedCourtsCount = normalizePocoCourtsCount(courtsCount);
  const pairsCount = getPocoPairsCount(normalizedCourtsCount);
  const roundsCount = getPocoRoundsCount(normalizedCourtsCount);
  const ids = pairs.map((pair) => pair.id).filter(Boolean);
  const pairIds = (shufflePairs ? shuffleList(ids) : ids).slice(0, pairsCount);
  if (pairIds.length < pairsCount) return [];
  const firstRound: AdminRoundMatch[] = Array.from(
    { length: normalizedCourtsCount },
    (_item, courtIndex) => ({
      dupla1Id: pairIds[courtIndex * 2],
      dupla2Id: pairIds[courtIndex * 2 + 1],
      score1: "",
      score2: "",
    }),
  );
  const rounds: AdminRoundMatch[][] = [firstRound];
  for (let roundIndex = 1; roundIndex < roundsCount; roundIndex += 1) {
    rounds.push(
      Array.from({ length: normalizedCourtsCount }, () =>
        buildEmptyPocoMatch(),
      ),
    );
  }
  return rebuildPocoRounds(rounds);
}
